import React, { useState, useRef, useEffect } from 'react';
import ImageCropper from './ImageCropper';
import getCroppedImg from '../services/getCroppedImg';
import { 
    X, Camera, Loader2, Save, User, Briefcase, Phone, 
    Heart, Droplets, MapPin, Calendar, Book
} from 'lucide-react';
import { FamilyMember } from '../types';
import { COUNTRY_CODES } from '../constants';

// Predefined locations (same as FamilyMap)
const LOCATION_SUGGESTIONS = [
  'New York', 'Los Angeles', 'Chicago', 'Houston', 'Phoenix', 'Philadelphia', 'San Antonio', 'San Diego', 'Dallas', 'San Jose', 'USA',
  'London', 'Manchester', 'Birmingham', 'Leeds', 'Glasgow', 'Liverpool', 'Newcastle',
  'Paris', 'Lyon', 'Marseille', 'Toulouse', 'Nice',
  'Tokyo', 'Osaka', 'Yokohama', 'Kobe', 'Kyoto',
  'Sydney', 'Melbourne', 'Brisbane', 'Perth', 'Adelaide',
  'Mumbai', 'Delhi', 'Bangalore', 'Bengaluru', 'Hyderabad', 'Chennai', 'Kolkata', 'Pune', 'Ahmedabad', 'Lucknow', 'Jaipur', 'Chinnalapatti',
  'Dubai', 'Abu Dhabi', 'Sharjah',
  'Toronto', 'Vancouver', 'Montreal', 'Calgary',
  'Berlin', 'Munich', 'Cologne', 'Frankfurt', 'Hamburg',
  'Singapore', 'Bangkok', 'Ho Chi Minh City', 'Hanoi', 'Manila',
  'Mexico City', 'Sao Paulo', 'Rio de Janeiro', 'Buenos Aires',
  'Moscow', 'St. Petersburg', 'Istanbul', 'Athens',
  'Cape Town', 'Johannesburg', 'Cairo', 'Lagos'
];

// Geocoding cache to avoid excessive API calls
const geoCache: Record<string, [number, number]> = {};

// Fetch coordinates from OpenStreetMap Nominatim API
const geocodeLocation = async (location: string): Promise<[number, number] | null> => {
  if (!location) return null;
  
  // Check cache first
  if (geoCache[location]) return geoCache[location];
  
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(location)}&format=json&limit=1`,
      { headers: { 'Accept': 'application/json' } }
    );
    const data = await response.json();
    
    if (data && data[0]) {
      const coords: [number, number] = [parseFloat(data[0].lat), parseFloat(data[0].lon)];
      geoCache[location] = coords; // Cache the result
      return coords;
    }
  } catch (err) {
    console.error('Geocoding error:', err);
  }
  return null;
};

// Fetch location suggestions from Nominatim API
const fetchLocationSuggestions = async (input: string): Promise<string[]> => {
  if (!input.trim() || input.length < 2) return [];
  
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(input)}&format=json&limit=8&addressdetails=0`,
      { headers: { 'Accept': 'application/json' } }
    );
    const data = await response.json();
    
        if (Array.isArray(data)) {
            // Only return the main place name (first part before comma)
            return data.map((item: any) => {
                const name = item.display_name || item.name;
                return name ? name.split(',')[0].trim() : '';
            }).filter(Boolean);
    }
  } catch (err) {
    console.error('Location search error:', err);
  }
  return [];
};

interface MemberFormModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: any) => Promise<void>;
    initialData?: Partial<FamilyMember>;
    isEditing: boolean;
    relativeName?: string;
    relationType?: string;
    members?: Record<string, FamilyMember>;
}

// --- HELPER: Smart Image Compression (Base64) ---
const compressImage = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX_WIDTH = 400; // Efficient size for avatars
        let width = img.width;
        let height = img.height;

        if (width > MAX_WIDTH) {
          height *= MAX_WIDTH / width;
          width = MAX_WIDTH;
        }

        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.drawImage(img, 0, 0, width, height);
            const compressedBase64 = canvas.toDataURL('image/jpeg', 0.6);
            resolve(compressedBase64);
        } else {
            reject(new Error("Canvas context failed"));
        }
      };
      img.onerror = (error) => reject(error);
    };
    reader.onerror = (error) => reject(error);
  });
};

const MemberFormModal: React.FC<MemberFormModalProps> = ({ isOpen, onClose, onSave, initialData, isEditing, relativeName, relationType, members = {} }) => {
    // Initialize State
    const [formData, setFormData] = useState<any>({
        name: '', gender: 'Male', dob: '', isDeceased: false,
        isMarried: false, weddingDate: '', demiseDate: '',
        location: '', profession: '', education: '',
        bloodGroup: '', motherTongue: '', phoneCountryCode: '+91', phoneNumber: '',
        email: '', bio: '', hobbies: '', avatarUrl: '',
        ...initialData
    });
    
    const [isSaving, setIsSaving] = useState(false);
    const [isProcessingImage, setIsProcessingImage] = useState(false);
    const [showCropper, setShowCropper] = useState(false);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
    const [rawImage, setRawImage] = useState<string | null>(null);
    const [locationSuggestions, setLocationSuggestions] = useState<string[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Load Data Effect
    useEffect(() => {
        if (isOpen) {
            if (isEditing && initialData) {
                const match = initialData.phoneNumber ? initialData.phoneNumber.match(/^(\+\d+)(.*)$/) : null;
                setFormData({
                    ...initialData,
                    phoneCountryCode: match ? match[1] : '+91',
                    phoneNumber: match ? match[2] : (initialData.phoneNumber || ''),
                    gender: initialData.gender || 'Male',
                    isDeceased: initialData.isDeceased ?? false,
                    maritalStatus: initialData.maritalStatus || (initialData.isMarried ? 'Married' : 'Single'),
                    isMarried: initialData.isMarried ?? false,
                    // Ensure education exists
                    education: initialData.education || '',
                    profession: initialData.profession || ''
                });
            } else {
                // Reset
                setFormData({
                    name: '', gender: 'Male', dob: '', isDeceased: false,
                    maritalStatus: 'Single', isMarried: false, weddingDate: '', divorceDate: '',
                    location: '', profession: '', education: '',
                    bloodGroup: '', motherTongue: '', phoneCountryCode: '+91', phoneNumber: '',
                    email: '', bio: '', hobbies: '', avatarUrl: ''
                });
            }
        }
    }, [isOpen, initialData, isEditing]);

    if (!isOpen) return null;

    const handleSave = async () => {
        if (!formData.name.trim()) return alert("Full Name is required.");
        if (!formData.gender) return alert("Gender is required.");
        
        // Validate phone number for duplicates
        if (formData.phoneNumber && formData.phoneNumber.trim()) {
            const finalPhone = `${formData.phoneCountryCode}${formData.phoneNumber}`;
            const isDuplicate = Object.values(members).some(member => 
                member.id !== (isEditing ? initialData?.id : null) && 
                member.phoneNumber && 
                member.phoneNumber === finalPhone
            );
            if (isDuplicate) {
                return alert("⚠️ This phone number is already registered in the database. Please use a different number or edit the existing profile.");
            }
        }
        
        setIsSaving(true);
        try {
            const finalPhone = formData.phoneNumber ? `${formData.phoneCountryCode}${formData.phoneNumber}` : '';
            await onSave({ ...formData, phoneNumber: finalPhone });
            onClose();
        } catch (error) {
            alert("Failed to save. Please try again.");
        } finally {
            setIsSaving(false);
        }
    };

    const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const reader = new FileReader();
            reader.onload = () => {
                setRawImage(reader.result as string);
                setShowCropper(true);
            };
            reader.readAsDataURL(e.target.files[0]);
        }
    };

    const onCropComplete = (croppedArea: any, croppedAreaPixels: any) => {
        setCroppedAreaPixels(croppedAreaPixels);
    };

    const handleCropSave = async () => {
        if (!rawImage || !croppedAreaPixels) return;
        setIsProcessingImage(true);
        try {
            const croppedBlob = await getCroppedImg(rawImage, croppedAreaPixels);
            const reader = new FileReader();
            reader.onloadend = () => {
                setFormData((prev: any) => ({ ...prev, avatarUrl: reader.result as string }));
                setShowCropper(false);
                setRawImage(null);
            };
            reader.readAsDataURL(croppedBlob);
        } catch (err) {
            alert('Failed to crop photo');
        } finally {
            setIsProcessingImage(false);
        }
    };

    return (
        <div style={{ zIndex: 99999 }} className="fixed inset-0 bg-black/60 flex items-end md:items-center justify-center p-0 pb-24 md:p-4 backdrop-blur-sm animate-fade-in">
            
            {/* CHANGED: Adjusted height to 'max-h-[85vh]' so it fits within the lifted space */}
            <div className="bg-white w-full md:max-w-lg md:rounded-3xl rounded-t-3xl h-full max-h-[85vh] md:h-[90vh] flex flex-col shadow-2xl animate-slide-up overflow-hidden">
                
                {/* Header */}
                <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 rounded-t-3xl shrink-0">
                    <div>
                        <h3 className="text-lg font-black text-gray-900">
                            {isEditing ? 'Edit Profile' : `Add ${relationType || 'Member'}`}
                        </h3>
                        {relativeName && <p className="text-[10px] text-indigo-600 font-bold mt-0.5">Connected to {relativeName}</p>}
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full text-gray-500"><X className="w-5 h-5" /></button>
                </div>

                {/* Content - Scrollable */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar min-h-0">
                    {/* Avatar - Smaller */}
                    <div className="flex justify-center">
                        <div className="relative group cursor-pointer" onClick={() => fileInputRef.current?.click()}>
                            <div className="w-20 h-20 rounded-full border-3 border-white shadow-md bg-gray-100 overflow-hidden relative">
                                {formData.avatarUrl ? (
                                    <img src={formData.avatarUrl} className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center bg-gray-200 text-gray-400"><User className="w-10 h-10" /></div>
                                )}
                                {isProcessingImage && (
                                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white"><Loader2 className="w-5 h-5 animate-spin" /></div>
                                )}
                            </div>
                            <div className="absolute bottom-0 right-0 bg-indigo-600 p-1.5 rounded-full text-white border-2 border-white shadow-md">
                                <Camera className="w-3.5 h-3.5" />
                            </div>
                            <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handlePhotoUpload} />
                            {showCropper && rawImage && (
                                <div className="fixed inset-0 z-[3000] flex items-center justify-center bg-black bg-opacity-70">
                                    <div className="bg-white rounded-xl p-4 max-w-lg w-full flex flex-col items-center">
                                        <ImageCropper
                                            image={rawImage}
                                            crop={crop}
                                            zoom={zoom}
                                            aspect={1}
                                            onCropChange={setCrop}
                                            onZoomChange={setZoom}
                                            onCropComplete={onCropComplete}
                                        />
                                        <div className="flex gap-4 mt-4">
                                            <button onClick={() => setShowCropper(false)} className="px-4 py-2 rounded bg-gray-200">Cancel</button>
                                            <button onClick={handleCropSave} className="px-4 py-2 rounded bg-indigo-600 text-white" disabled={isProcessingImage}>{isProcessingImage ? 'Saving...' : 'Save'}</button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="space-y-3">
                        {/* Name Field */}
                        <div>
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 mb-1 block">Full Name <span className="text-red-500">*</span></label>
                            <div className="flex items-center bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 focus-within:ring-2 focus-within:ring-indigo-100 transition-all">
                                <User className="w-4 h-4 text-gray-400 mr-2 shrink-0" />
                                <input type="text" className="bg-transparent w-full text-sm font-bold outline-none text-gray-900" placeholder="e.g. Rahul Sharma" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
                            </div>
                        </div>

                        {/* Gender & Status - Compact */}
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 mb-1 block">Gender <span className="text-red-500">*</span></label>
                                <div className="flex bg-gray-50 p-1 rounded-xl border border-gray-200 gap-1">
                                    {[
                                      { value: 'Male', label: 'M' },
                                      { value: 'Female', label: 'F' },
                                      { value: 'Other', label: 'O' }
                                    ].map(g => (
                                        <button key={g.value} onClick={() => setFormData({...formData, gender: g.value})} className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${formData.gender === g.value ? 'bg-white shadow text-indigo-600' : 'text-gray-400'}`} title={g.value}>{g.label}</button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 mb-1 block">Status</label>
                                <div className="flex bg-gray-50 p-1 rounded-xl border border-gray-200">
                                    <button onClick={() => setFormData({...formData, isDeceased: false})} className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${!formData.isDeceased ? 'bg-emerald-100 text-emerald-700' : 'text-gray-400'}`}>Alive</button>
                                    <button onClick={() => setFormData({...formData, isDeceased: true})} className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${formData.isDeceased ? 'bg-slate-800 text-white' : 'text-gray-400'}`}>Late</button>
                                </div>
                            </div>
                        </div>

                        {/* DOB & Location - Side by side */}
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 mb-1 block">Date of Birth</label>
                                <div className="flex items-center bg-gray-50 border border-gray-200 rounded-xl px-2 py-2">
                                    <Calendar className="w-3.5 h-3.5 text-gray-400 mr-1.5 shrink-0" />
                                    <input 
                                        type="date" 
                                        max={new Date().toISOString().split("T")[0]}
                                        className="bg-transparent w-full text-xs font-bold outline-none text-gray-900" 
                                        value={formData.dob} 
                                        onChange={e => setFormData({...formData, dob: e.target.value})} 
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="text-[10px] font-black text-indigo-500 uppercase tracking-widest ml-1 mb-1 block flex items-center gap-1"><MapPin className="w-3 h-3" /> Location</label>
                                <div className="relative">
                                  <input 
                                    type="text" 
                                    placeholder="Search any city worldwide..." 
                                    className="w-full bg-indigo-50 border border-indigo-100 rounded-xl px-2 py-2 text-xs font-bold text-indigo-900 outline-none placeholder:text-indigo-300" 
                                    value={formData.location} 
                                    onChange={async (e) => {
                                      const value = e.target.value;
                                      setFormData({...formData, location: value});
                                      if (value.length >= 2) {
                                        const suggestions = await fetchLocationSuggestions(value);
                                        setLocationSuggestions(suggestions);
                                      } else {
                                        setLocationSuggestions([]);
                                      }
                                    }}
                                    onFocus={async () => {
                                      if (formData.location && formData.location.length >= 2) {
                                        const suggestions = await fetchLocationSuggestions(formData.location);
                                        setLocationSuggestions(suggestions);
                                      }
                                    }}
                                  />
                                                                    {locationSuggestions.length > 0 && (
                                                                        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-indigo-200 rounded-lg shadow-lg z-20 max-h-40 overflow-y-auto">
                                                                            {locationSuggestions.map((loc, idx) => {
                                                                                const mainPlace = loc.split(',')[0].trim();
                                                                                return (
                                                                                    <button
                                                                                        key={mainPlace + idx}
                                                                                        type="button"
                                                                                        onClick={() => {
                                                                                            setFormData({...formData, location: mainPlace});
                                                                                            setLocationSuggestions([]);
                                                                                        }}
                                                                                        className="w-full text-left px-3 py-2 text-xs font-bold text-indigo-900 hover:bg-indigo-50 border-b border-indigo-100 last:border-b-0 transition-colors"
                                                                                    >
                                                                                        {mainPlace}
                                                                                    </button>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Marital Status - Compact */}
                        <div className="grid grid-cols-3 gap-2">
                            <div className="col-span-3">
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 mb-1 block">Marital Status</label>
                                <div className="flex bg-gray-50 p-1 rounded-xl border border-gray-200 gap-1">
                                    <button 
                                        onClick={() => setFormData({...formData, maritalStatus: 'Single', isMarried: false, weddingDate: '', divorceDate: ''})} 
                                        className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${formData.maritalStatus === 'Single' ? 'bg-white shadow text-blue-600' : 'text-gray-400'}`}
                                    >
                                        Single
                                    </button>
                                    <button 
                                        onClick={() => setFormData({...formData, maritalStatus: 'Married', isMarried: true, divorceDate: ''})} 
                                        className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${formData.maritalStatus === 'Married' ? 'bg-white shadow text-pink-600' : 'text-gray-400'}`}
                                    >
                                        Married
                                    </button>
                                    <button 
                                        onClick={() => setFormData({...formData, maritalStatus: 'Divorced', isMarried: false})} 
                                        className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${formData.maritalStatus === 'Divorced' ? 'bg-white shadow text-red-600' : 'text-gray-400'}`}
                                    >
                                        Divorced
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Date Fields - Context Aware */}
                        <div className="grid grid-cols-2 gap-3">
                            {formData.isDeceased ? (
                                <>
                                    <div className="animate-fade-in mb-2">
                                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 mb-1 block">Demise Date</label>
                                        <div className="flex items-center bg-gray-50 border border-gray-200 rounded-xl px-2 py-2">
                                            <Heart className="w-3.5 h-3.5 text-gray-400 mr-1.5 shrink-0" />
                                            <input type="date" max={new Date().toISOString().split("T")[0]} className="bg-transparent w-full text-xs font-bold outline-none text-gray-900" value={formData.demiseDate} onChange={e => setFormData({...formData, demiseDate: e.target.value})} />
                                        </div>
                                    </div>
                                    {formData.maritalStatus === 'Married' && (
                                        <div className="animate-fade-in">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 mb-1 block">Wedding Date</label>
                                            <div className="flex items-center bg-gray-50 border border-gray-200 rounded-xl px-2 py-2">
                                                <Heart className="w-3.5 h-3.5 text-pink-400 mr-1.5 shrink-0" />
                                                <input type="date" max={new Date().toISOString().split("T")[0]} className="bg-transparent w-full text-xs font-bold outline-none text-gray-900" value={formData.weddingDate} onChange={e => setFormData({...formData, weddingDate: e.target.value})} />
                                            </div>
                                        </div>
                                    )}
                                    {formData.maritalStatus === 'Divorced' && (
                                        <div className="animate-fade-in">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 mb-1 block">Divorce Date</label>
                                            <div className="flex items-center bg-gray-50 border border-gray-200 rounded-xl px-2 py-2">
                                                <Heart className="w-3.5 h-3.5 text-red-400 mr-1.5 shrink-0 opacity-50 scale-x-150" style={{transform: 'scaleX(1.5)'}} />
                                                <input type="date" max={new Date().toISOString().split("T")[0]} className="bg-transparent w-full text-xs font-bold outline-none text-gray-900" value={formData.divorceDate} onChange={e => setFormData({...formData, divorceDate: e.target.value})} />
                                            </div>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <>
                                    {formData.maritalStatus === 'Married' && (
                                        <div className="col-span-2 animate-fade-in">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 mb-1 block">Wedding Date</label>
                                            <div className="flex items-center bg-gray-50 border border-gray-200 rounded-xl px-2 py-2">
                                                <Heart className="w-3.5 h-3.5 text-pink-400 mr-1.5 shrink-0" />
                                                <input type="date" max={new Date().toISOString().split("T")[0]} className="bg-transparent w-full text-xs font-bold outline-none text-gray-900" value={formData.weddingDate} onChange={e => setFormData({...formData, weddingDate: e.target.value})} />
                                            </div>
                                        </div>
                                    )}
                                    {formData.maritalStatus === 'Divorced' && (
                                        <div className="col-span-2 animate-fade-in">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 mb-1 block">Divorce Date</label>
                                            <div className="flex items-center bg-gray-50 border border-gray-200 rounded-xl px-2 py-2">
                                                <Heart className="w-3.5 h-3.5 text-red-400 mr-1.5 shrink-0 opacity-50" style={{transform: 'scaleX(1.5)'}} />
                                                <input type="date" max={new Date().toISOString().split("T")[0]} className="bg-transparent w-full text-xs font-bold outline-none text-gray-900" value={formData.divorceDate} onChange={e => setFormData({...formData, divorceDate: e.target.value})} />
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    {/* Professional & Contact - Combined Section */}
                    <div className="space-y-3 pt-2 border-t border-gray-100">
                        <div className="flex items-center gap-2 text-indigo-600 pb-1">
                            <Briefcase className="w-3.5 h-3.5" /> <span className="text-[10px] font-black uppercase">Professional & Contact</span>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 mb-1 block">Blood Group</label>
                                <div className="flex items-center bg-gray-50 border border-gray-200 rounded-xl px-2 py-2">
                                    <Droplets className="w-3.5 h-3.5 text-red-500 mr-1.5 shrink-0" />
                                    <select className="bg-transparent w-full text-xs font-bold outline-none text-gray-900" value={formData.bloodGroup} onChange={e => setFormData({...formData, bloodGroup: e.target.value})}>
                                        <option value="">Select</option>
                                        {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(bg => <option key={bg} value={bg}>{bg}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 mb-1 block">Profession</label>
                                <div className="flex items-center bg-gray-50 border border-gray-200 rounded-xl px-2 py-2">
                                    <Briefcase className="w-3.5 h-3.5 text-gray-400 mr-1.5 shrink-0" />
                                    <input type="text" placeholder="e.g. Engineer" className="bg-transparent w-full text-xs font-medium outline-none" value={formData.profession} onChange={e => setFormData({...formData, profession: e.target.value})} />
                                </div>
                            </div>
                        </div>

                        <div>
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 mb-1 block">Education</label>
                            <div className="flex items-center bg-gray-50 border border-gray-200 rounded-xl px-2 py-2">
                                <Book className="w-3.5 h-3.5 text-gray-400 mr-1.5 shrink-0" />
                                <input type="text" placeholder="e.g. MBA, Stanford" className="bg-transparent w-full text-xs font-medium outline-none" value={formData.education} onChange={e => setFormData({...formData, education: e.target.value})} />
                            </div>
                        </div>

                        <div>
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 mb-1 block flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> Phone</label>
                            <div className="relative">
                                <input 
                                    type="tel" 
                                    placeholder="e.g. +91 9876543210 or +1 2025550123"
                                    maxLength={20}
                                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs font-bold outline-none focus:ring-2 focus:ring-indigo-500" 
                                    value={`${formData.phoneCountryCode}${formData.phoneNumber}`}
                                    onChange={(e) => {
                                        const input = e.target.value.trim();
                                        
                                        // Allow empty input
                                        if (!input) {
                                            setFormData({...formData, phoneCountryCode: '', phoneNumber: ''});
                                            return;
                                        }
                                        
                                        // Check if input starts with + (country code)
                                        if (input.startsWith('+')) {
                                            // Extract country code (up to 3 digits after +)
                                            const match = input.match(/^(\+\d{1,3})(.*)$/);
                                            if (match) {
                                                const code = match[1];
                                                const number = match[2].replace(/\D/g, ''); // Remove non-digits from number part
                                                setFormData({...formData, phoneCountryCode: code, phoneNumber: number});
                                            } else {
                                                // Just + with no digits yet
                                                setFormData({...formData, phoneCountryCode: input, phoneNumber: ''});
                                            }
                                        } else {
                                            // No + sign, just digits - use empty country code or default to +91
                                            const number = input.replace(/\D/g, '');
                                            setFormData({...formData, phoneCountryCode: '+91', phoneNumber: number});
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer Buttons - Always Visible at Bottom */}
                <div className="p-4 border-t-2 border-gray-200 bg-white rounded-b-3xl shrink-0 flex gap-3 shadow-2xl">
                    <button onClick={onClose} className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-bold text-sm transition-colors">Cancel</button>
                    <button onClick={handleSave} disabled={isSaving || isProcessingImage} className="flex-[2] py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-black text-sm shadow-lg shadow-indigo-200 transition-all flex items-center justify-center gap-2 disabled:opacity-50">
                        {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        {isEditing ? 'Save Changes' : 'Add Member'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MemberFormModal;